// Centralized prop firm data — used by /prop-firms and /prop-firms/:a-vs-:b

export interface PropFirm {
  id: string;
  name: string;
  logo?: string;
  featured?: boolean;
  accountSizes: string[];
  profitSplit: string;
  maxDrawdown: string;
  dailyDrawdown: string;
  minTradingDays: number;
  payoutFrequency: string;
  platforms: string[];
  evaluationFee: string;
  monthlyFee: string;
  profitTarget: string;
  scaling: boolean;
  newsTrading: boolean;
  weekendHolding: boolean;
  pros: string[];
  cons: string[];
  specialFeatures: string[];
}

export const propFirms: PropFirm[] = [
  {
    id: "apex",
    name: "Apex Trader Funding",
    featured: true,
    accountSizes: ["$25K", "$50K", "$100K", "$150K", "$250K", "$300K"],
    profitSplit: "100% (Sim Funded, new product)",
    maxDrawdown: "6%",
    dailyDrawdown: "3%",
    minTradingDays: 5,
    payoutFrequency: "Every 5 trading days",
    platforms: ["NinjaTrader", "TradingView", "Rithmic"],
    evaluationFee: "$147-$597",
    monthlyFee: "$0",
    profitTarget: "8%",
    scaling: true,
    newsTrading: true,
    weekendHolding: true,
    pros: ["No monthly fees", "Weekend holding allowed", "News trading permitted", "Fast payouts", "Generous scaling plan"],
    cons: ["Stricter daily loss limits"],
    specialFeatures: ["Free retries after 30 days", "Lifetime account after 4 payouts", "No time limits on evaluation"],
  },
  {
    id: "ftmo",
    name: "FTMO",
    accountSizes: ["$10K", "$25K", "$50K", "$100K", "$200K"],
    profitSplit: "80% (90% after 10% profit)",
    maxDrawdown: "10%",
    dailyDrawdown: "5%",
    minTradingDays: 10,
    payoutFrequency: "Monthly",
    platforms: ["MT4", "MT5", "cTrader", "DXTrade"],
    evaluationFee: "$155-$1080",
    monthlyFee: "$0",
    profitTarget: "8% + 5%",
    scaling: true,
    newsTrading: false,
    weekendHolding: false,
    pros: ["Well-established", "Multiple platforms", "Good support", "Scaling available"],
    cons: ["No news trading", "No weekend holding", "Two-step challenge"],
    specialFeatures: ["Free FTMO Trader app", "Performance Coach", "Risk management tools"],
  },
  {
    id: "topstep",
    name: "TopstepTrader",
    accountSizes: ["$50K", "$100K", "$150K"],
    profitSplit: "80% (90% after 5 payouts)",
    maxDrawdown: "$3K-$6K",
    dailyDrawdown: "$1.5K-$3K",
    minTradingDays: 0,
    payoutFrequency: "Bi-weekly",
    platforms: ["NinjaTrader", "TradingView", "Quantower"],
    evaluationFee: "$165-$375",
    monthlyFee: "$150-$375",
    profitTarget: "$3K-$6K",
    scaling: true,
    newsTrading: true,
    weekendHolding: true,
    pros: ["Futures-focused", "Real money from day 1", "News trading allowed", "Good for active traders"],
    cons: ["Monthly subscription fees", "Lower profit targets", "Position size restrictions"],
    specialFeatures: ["Express programs", "Live trading rooms", "Education resources"],
  },
  {
    id: "the5ers",
    name: "The5%ers",
    accountSizes: ["$4K", "$6K", "$20K", "$100K"],
    profitSplit: "80% (up to 100%)",
    maxDrawdown: "4%",
    dailyDrawdown: "4%",
    minTradingDays: 6,
    payoutFrequency: "Bi-weekly",
    platforms: ["MT4", "MT5"],
    evaluationFee: "$49-$845",
    monthlyFee: "$0",
    profitTarget: "12%",
    scaling: true,
    newsTrading: true,
    weekendHolding: true,
    pros: ["Progressive profit split", "Lower account minimums", "News trading allowed", "Weekend holding"],
    cons: ["High profit targets", "Strict drawdown rules", "Limited platforms"],
    specialFeatures: ["Instant funding option", "Progressive profit split up to 100%", "No time limits"],
  },
  {
    id: "funded-next",
    name: "Funded Next",
    accountSizes: ["$6K", "$15K", "$25K", "$50K", "$100K", "$200K"],
    profitSplit: "80% (90% after first payout)",
    maxDrawdown: "12%",
    dailyDrawdown: "5%",
    minTradingDays: 5,
    payoutFrequency: "Bi-weekly",
    platforms: ["MT4", "MT5", "DXTrade", "cTrader"],
    evaluationFee: "$49-$1599",
    monthlyFee: "$0",
    profitTarget: "8% + 5%",
    scaling: true,
    newsTrading: true,
    weekendHolding: true,
    pros: ["Competitive fees", "Multiple challenge types", "News trading allowed", "Good scaling"],
    cons: ["Two-step process", "Strict consistency rules"],
    specialFeatures: ["1-step and 2-step challenges", "Evaluation reset options", "Copy trading allowed"],
  },
  {
    id: "myfundedfutures",
    name: "MyFundedFutures",
    accountSizes: ["$50K", "$100K", "$150K"],
    profitSplit: "100% up to $10K, then 90%",
    maxDrawdown: "$2K-$5K (trailing)",
    dailyDrawdown: "None on funded",
    minTradingDays: 0,
    payoutFrequency: "Bi-weekly (Starter); on-demand (Expert)",
    platforms: ["NinjaTrader", "Tradovate", "TradingView", "Rithmic"],
    evaluationFee: "$80-$300 (one-time)",
    monthlyFee: "$0",
    profitTarget: "$3K-$9K",
    scaling: true,
    newsTrading: true,
    weekendHolding: false,
    pros: ["No minimum trading days", "100% first-$10K profit", "Multiple plan tiers (Starter/Expert/Milestone)", "One-time evaluation fee"],
    cons: ["Trailing drawdown on evaluation", "No weekend holding on Starter plan"],
    specialFeatures: ["Starter, Expert, and Milestone plans", "No consistency rule on Expert plan", "Same-day payouts on Expert plan"],
  },
  {
    id: "tradeday",
    name: "TradeDay",
    accountSizes: ["$25K", "$50K", "$100K", "$150K"],
    profitSplit: "90%",
    maxDrawdown: "$1.5K-$4.5K (EOD)",
    dailyDrawdown: "Soft (encouraged)",
    minTradingDays: 7,
    payoutFrequency: "Twice monthly",
    platforms: ["NinjaTrader", "Tradovate", "TradingView", "Rithmic"],
    evaluationFee: "$99-$399",
    monthlyFee: "$0",
    profitTarget: "$1.5K-$9K",
    scaling: true,
    newsTrading: true,
    weekendHolding: false,
    pros: ["End-of-day drawdown (no intraday trail)", "90% profit split from day one", "Real-trader founded, education-focused", "Strong Trustpilot reviews"],
    cons: ["7-day minimum trading days", "No weekend holding"],
    specialFeatures: ["EOD drawdown calculation", "Free retries on certain plans", "Trader development resources"],
  },
  {
    id: "take-profit-trader",
    name: "Take Profit Trader",
    accountSizes: ["$25K", "$50K", "$75K", "$100K", "$150K"],
    profitSplit: "80% first $10K, then 90%",
    maxDrawdown: "$1.5K-$4.5K (EOD)",
    dailyDrawdown: "None",
    minTradingDays: 5,
    payoutFrequency: "Bi-weekly",
    platforms: ["NinjaTrader", "Tradovate", "TradingView", "Rithmic"],
    evaluationFee: "$150-$650",
    monthlyFee: "$0",
    profitTarget: "$1.5K-$9K",
    scaling: true,
    newsTrading: true,
    weekendHolding: false,
    pros: ["End-of-day drawdown", "No daily loss limit", "Generous activation pricing", "Fast funding after evaluation"],
    cons: ["80/20 split until first $10K", "5-day minimum"],
    specialFeatures: ["PRO account upgrade with higher split", "Simple one-step evaluation", "Direct payout via Riseworks"],
  },
  {
    id: "elite-trader-funding",
    name: "Elite Trader Funding",
    accountSizes: ["$25K", "$50K", "$100K", "$150K", "$250K"],
    profitSplit: "80% / 90% (plan dependent)",
    maxDrawdown: "Static or trailing (plan dependent)",
    dailyDrawdown: "None on most plans",
    minTradingDays: 0,
    payoutFrequency: "Bi-weekly",
    platforms: ["NinjaTrader", "Tradovate", "TradingView", "Rithmic"],
    evaluationFee: "$50-$650",
    monthlyFee: "$0",
    profitTarget: "$1.5K-$15K",
    scaling: true,
    newsTrading: true,
    weekendHolding: true,
    pros: ["Multiple plan structures (Static, Fast Track, Diamond Hands)", "Weekend holding allowed", "No minimum trading days on most plans", "Frequent discount promotions"],
    cons: ["Plan complexity can confuse beginners", "Rules vary widely between plans"],
    specialFeatures: ["Static drawdown plans available", "Diamond Hands plan with no scaling limits", "Fast Track for quick evaluation"],
  },
];

export const getFirmById = (id: string): PropFirm | undefined =>
  propFirms.find((f) => f.id.toLowerCase() === id.toLowerCase());

export const firmSlug = (firm: PropFirm) => firm.id;

/**
 * Editorial ranking: Apex Trader Funding is always ranked first regardless of
 * other sort criteria. After Apex, firms are returned in their declaration
 * order in `propFirms`. Use this anywhere you list firms by recommendation
 * (homepage grid, comparison table, best-of pages) so the site has a single
 * source of truth for ordering.
 */
export const getRankedFirms = (): PropFirm[] => {
  const apex = propFirms.find((f) => f.id === "apex");
  const others = propFirms.filter((f) => f.id !== "apex");
  return apex ? [apex, ...others] : others;
};

